import { CustomerGenderType, CustomersPhoneNumberType, CustomerType } from "@/types/customer/customer.type";
import { CustomerPhoneInterface, CustomerPhoneModule } from "./phones";
import { CustomerGenderModule } from "./genders";
import { CustomerAddressModule } from "./address";
import moment from "moment-timezone";
import Storage from "@/services/storage";
import { StorageConstants } from "@/enums/storage.enum";
import Tag from "./tags";
import { CustomerInterface } from "@/interface/customer/customers.interface";
import { PriceInterface } from "@/interface/business/item.interface";
import { PriceMoney } from "@/modules/location";
import { CUSTOMER_SOURCE } from "@/enums/customer.enums";

/***
 * Customer module
 */
export class Customer implements CustomerInterface {
  id: string = "";
  customer_name: string = "";
  first_name: string = "";
  last_name: string = "";
  email: string = "";
  gender: string | CustomerGenderType | null = "";
  tags: Array<Object> = [];
  created_at: Date = new Date();
  updated_at: Date = new Date();
  last_visit: Date = new Date();
  number_of_orders?: Number;
  amount_spent?: Number;
  average_spend?: Number;
  selected: Boolean = false;
  dob: string = "";
  month?: string;
  day?: string;
  year?: string;
  comments: string = "";
  country: string = "";
  addresses: Array<any> = [];
  phone_numbers: Array<any> = [];
  balance?: PriceInterface;
  source?: string = "";

  public static MODULE_NAME: string = "customers/customer";

  constructor(data: CustomerType = null) {
    if (!!data) {
      this.id = data.id;
      this.first_name = data.first_name || "";
      this.last_name = data.last_name || "";

      this.customer_name = this.fullName();
      this.email = data.email || "";
      this.gender = data.gender;
      this.tags = data.tags || [];
      this.created_at = data.created_at || new Date();
      this.updated_at = data.updated_at || new Date();
      this.last_visit = new Date();
      this.number_of_orders = 1;
      this.amount_spent = 1;
      this.average_spend = 1;
      this.selected = false;
      this.dob = data.dob || "";
      this.country = data?.country || Storage.get(StorageConstants.BUSINESS).country;
      this.comments = data?.comments || "";
      this.source = data.source;
      this.balance = new PriceMoney({ amount: data?.balance || 0 });

      this.setupDefaultCustomerPhones(data);
      this.setupDefaultCustomerAddress(data);
    } else {
      this.phone_numbers = Customer.initPhoneNumbers();
    }
  }

  /**
   * Setup customer default Phones
   * @param data
   * @private
   */
  private setupDefaultCustomerPhones(data: any): void {
    if (!data) {
      this.phone_numbers = [new CustomerPhoneModule()];
    }
    // Default behavior
    let phones: any = data?.phone_numbers || [];

    // Special case for customer in order
    if (data?.phone) {
      phones = [data?.phone];
    }

    //share customer phone numbers
    this.phone_numbers = phones?.map((phone) => new CustomerPhoneModule(phone)) || Customer.initPhoneNumbers();
  }

  /**
   * Setup customer default Address
   * @param data
   * @private
   */
  private setupDefaultCustomerAddress(data: any): void {
    // Default behavior
    let address: any = data?.addresses || [];

    // Special case for customer in order
    if (data?.address) {
      address = [data?.address];
    }

    //share customer address
    this.addresses = address?.map((address) => new CustomerAddressModule(address)) || [];
  }

  /**
   * Get row data to show row data in customer list
   * @return { Object }
   * @memberof Customer
   */
  public getRowData(): Object {
    return {
      customer_name: `${this.first_name} ${this.last_name}`,
      phone_numbers: this.phone_numbers,
      primary_phone_number: this.primary_phone_number(),
      tags: this.tagsToChips(),
      last_visit: moment(new Date(this.last_visit)).format("YYYY/MM/DD"),
      number_of_orders: this.number_of_orders,
      amount_spent: this.amount_spent,
      average_spend: this.average_spend,
      selected: this.selected,
      id: this.id,
      source: this.source,
    };
  }

  /**
   * Prepare customer object to use it on the create form
   * @return {Object}
   * @memberof Customer
   */
  public toFormData(): Object {
    const phone_numbers = this.preparePhoneNumbersForm();
    const { year, month, day } = this.prepareDateOfBirthForm();
    const addresses = this.addresses.map((address) => new CustomerAddressModule(address));

    return {
      id: this.id,
      email: this.email,
      gender: this.prepareGenderToData(),
      first_name: this.first_name,
      last_name: this.last_name,
      created_at: moment(new Date(this.created_at)).format("YYYY/MM/DD"),
      updated_at: moment(new Date(this.updated_at)).format("YYYY/MM/DD"),
      last_visit: moment(new Date(this.last_visit)).format("YYYY/MM/DD"),
      dob: this.dob,
      comments: this.comments,
      country: this.country,
      tags: this.prepareTagsForm(),
      customer_name: this.customer_name,
      source: this.source,
      addresses,
      year,
      month,
      day,
      phone_numbers,
    };
  }

  /**
   *to prepare customer object to use it submit form
   * @return {*}  {Object}
   * @memberof Customer
   */
  public toJson(): Object {
    const phone_numbers = this.preparePhoneNumbersJson();
    const { year, month, day } = this.prepareDateOfBirthForm();
    const dob = window.moment(new Date(`${year || ""}-${month || ""}-${day || ""}`));
    const tags = this.prepareTagsJson();
    const addresses = this.prepareAddressesToJson();
    const gender = this.prepareGenderToJson();

    return {
      id: this.id,
      first_name: this.first_name,
      last_name: this.last_name,
      email: this.email,
      country: this.country,
      comments: this.comments,
      source: this.source ?? CUSTOMER_SOURCE.CALLCENTER,
      gender,
      addresses,
      phone_numbers,
      tags,
      day,
      month,
      year,
      dob: dob.isValid() ? dob.format("YYYY-MM-DD") : null,
    };
  }

  private static initPhoneNumbers(): Array<CustomersPhoneNumberType> {
    const phone = new CustomerPhoneModule({ is_primary: true }).getData();
    return [phone];
  }

  private preparePhoneNumbersForm(): Array<CustomerPhoneInterface> {
    if (!!this.phone_numbers.length) {
      return this.phone_numbers.map((phone) => {
        const newPhone = new CustomerPhoneModule(phone);
        return newPhone; //.getData();
      });
    }

    const newPhone = new CustomerPhoneModule({}).getData();
    return [newPhone];
  }

  private preparePhoneNumbersJson(): Array<CustomersPhoneNumberType> {
    return this.phone_numbers.map((phone) => {
      const newPhone = new CustomerPhoneModule(phone);
      return newPhone.toJson();
    });
  }

  private prepareTagsForm(): Array<Tag> {
    return this.tags.map((tag: any) => {
      return new Tag(tag);
    });
  }

  private prepareTagsJson(): Array<string> {
    return this.tags.map((tag: any) => {
      return tag.toJson();
    });
  }

  private prepareDateOfBirthForm(): any {
    const dob = this.dob?.toString()?.split("-") || [];

    return {
      year: dob?.[0] || "",
      month: dob?.[1] || "",
      day: dob?.[2] || "",
    };
  }

  public tagsToChips(): any {
    return this.tags.length ? this.tags.map((tag: any) => tag.name) : [];
  }

  public prepareGenderToData(): any {
    const gender = new CustomerGenderModule(this.gender);
    return gender.toData();
  }

  public prepareGenderToJson(): string {
    const gender = new CustomerGenderModule(this.gender);
    return gender.toJson();
  }

  public prepareAddressesToJson(): any {
    return this.addresses.map((address) => new CustomerAddressModule(address).getAddress());
  }

  public primary_phone_number(): CustomersPhoneNumberType {
    return this.phone_numbers.filter((phone) => !!phone.is_primary)[0];
  }

  public primary_address() {
    const address = this.addresses.find((address) => !!address.is_primary) || {};
    return new CustomerAddressModule(address);
  }

  public getPrimaryAddress(): any {
    const address = this.addresses.find((address) => !!address.is_primary) || {};
    return new CustomerAddressModule(address);
  }

  public removeCustomerId() {
    delete this.id;
  }

  public getArea(): string {
    return this.primary_address().area_id;
  }

  /**
   * Customer full name
   * @return {string}
   */
  public fullName(): string {
    return `${this?.first_name} ${this?.last_name}`;
  }

  /**
   * Short Name (2 char)
   * @return {string}
   */
  public shortName(): string {
    return `${this?.first_name.charAt(0).toUpperCase()}${this.last_name.charAt(0).toUpperCase()}`;
  }
}
